/**
 * Duplicate accounts: the same company entered more than once (the Pipedrive export had 644
 * groups of repeated names, mostly from list imports). Read-only: it tells the team what to
 * clean up in zInspector before and after the migration.
 *
 * Each group is one of three kinds, because each needs a different action:
 * - "empty copies": at most one copy has any history, so the others can be deleted;
 * - "merge": two or more copies have history, so they need merging;
 * - "branches": the copies are in different markets, so they are probably real branches.
 */
import type { ZcrmAccount } from "./accounts";

export type DuplicateKind = "empty copies" | "merge" | "branches";

export interface DuplicateGroup {
  key: string;
  kind: DuplicateKind;
  accounts: ZcrmAccount[];
}

/** Legal forms and certifications that don't make two companies different. */
const NOISE_WORDS = new Set([
  "the",
  "llc",
  "inc",
  "co",
  "corp",
  "corporation",
  "ltd",
  "pllc",
  "lp",
  "llp",
  "crmc",
  "mpm",
  "rmp",
  "cpm",
]);

export const normalizeAccountName = (name: string): string =>
  name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((word) => word && !NOISE_WORDS.has(word))
    .join(" ");

const hasHistory = (account: ZcrmAccount) =>
  account.view !== "prospects" ||
  !account.is_untouched ||
  account.nb_contacts > 0;

const KIND_ORDER: DuplicateKind[] = ["merge", "empty copies", "branches"];

export const findDuplicateAccounts = (
  accounts: ZcrmAccount[],
): DuplicateGroup[] => {
  const byName = new Map<string, ZcrmAccount[]>();
  for (const account of accounts) {
    const key = normalizeAccountName(account.name);
    if (!key) continue;
    byName.set(key, [...(byName.get(key) ?? []), account]);
  }
  return [...byName.entries()]
    .filter(([, group]) => group.length > 1)
    .map(([key, group]): DuplicateGroup => {
      const markets = new Set(group.map((a) => a.market).filter(Boolean));
      const withHistory = group.filter(hasHistory).length;
      const kind: DuplicateKind =
        markets.size > 1
          ? "branches"
          : withHistory > 1
            ? "merge"
            : "empty copies";
      return { key, kind, accounts: group };
    })
    .sort(
      (a, b) =>
        KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) ||
        b.accounts.length - a.accounts.length ||
        a.key.localeCompare(b.key),
    );
};
